import { reportSiteGroupKey } from '../../lib/reportSiteIdentity';
import React from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Map as MapIcon,
  Clock,
  Search,
  ArrowUpRight,
  Zap,
  Thermometer,
  Globe,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Map, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import { DeleteSiteDialog } from './DeleteSiteDialog';
import { siteSummaryScore } from '../../lib/siteSummaryScore';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { tierFromScore } from '../../lib/reportVerdict';
import { compareSitesByReadiness, type EvidenceReadinessTier } from '../../lib/evidenceReadiness';
import { getBtmDecisionState, getDecisionStateLabel, type BtmDecisionState, getTrustCue } from '../../lib/btmDecision';
import { BtmStrategyMapLegend } from './BtmStrategyMapLegend';
import {
  buildPortfolioMapPins,
  latestUsableSavedReportForSite,
  siteBtmVerdictReport,
  siteBtmVerdictTier,
  type PortfolioMapSitePin,
} from '../../lib/siteBtmMapVerdict';
import { btmStrategyTierLabel, pinColorsForVerdictTier } from '../../lib/btmStrategyMapLegend';
import { effectivePower, bucketMidpointMw } from '../../lib/projectIntent';
import { LayoutDashboard } from 'lucide-react';

/** Map wrapper that catches Google Maps loading errors instead of crashing the app. */
class MapErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-muted/30 text-center p-8">
          <div className="space-y-2">
            <Globe className="w-8 h-8 text-text-disabled mx-auto" />
            <p className="type-meta">Map unavailable — check Google Maps API key configuration</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function DashboardMap({ pins, onPinClick }: { pins: PortfolioMapSitePin[]; onPinClick: (siteId: string) => void }) {
  return (
    <MapErrorBoundary>
      <Map
        mapId="bf51a910020fa25a"
        defaultCenter={{ lat: 39.8283, lng: -98.5795 }}
        defaultZoom={4}
        gestureHandling={'greedy'}
        disableDefaultUI={false}
        mapTypeId={'hybrid'}
      >
        {pins.map(pin => {
          const colors = pinColorsForVerdictTier(pin.tier);
          const tierLabel = pin.tier === 'none' ? 'Not yet evaluated' : btmStrategyTierLabel(pin.tier);
          return (
            <AdvancedMarker
              key={pin.siteId}
              position={pin.position}
              onClick={() => onPinClick(pin.siteId)}
              title={`${pin.siteName} — ${tierLabel}`}
            >
              <Pin
                background={colors.background}
                glyphColor={'#fff'}
                borderColor={colors.borderColor}
              />
            </AdvancedMarker>
          );
        })}
      </Map>
    </MapErrorBoundary>
  );
}

/**
 * DR2 — Tier-distribution sparkbar. Renders a thin horizontal stacked bar
 * coloured green / amber / red / muted in proportion to the per-tier site
 * count, with a one-line "X green · Y amber · Z red" summary underneath.
 * Empty state (`total === 0`) prints an em-dash + helper text — the post-SF
 * replacement for the orphaned numeric "Average Site Score".
 */
function TierDistributionBar({
  green,
  amber,
  red,
  untiered,
  total,
}: {
  green: number;
  amber: number;
  red: number;
  untiered: number;
  total: number;
}) {
  if (total <= 0) {
    return (
      <div className="space-y-1" data-testid="tier-distribution-empty">
        <p className="text-3xl font-bold text-text-primary">
          <span className="text-text-muted/50">—</span>
        </p>
        <p className="type-meta uppercase tracking-widest text-text-muted">
          Generate a report to see tier distribution
        </p>
      </div>
    );
  }
  const pct = (n: number) => (n / total) * 100;
  // Title is human-readable + machine-parseable for the regression suite.
  const title = `${green} green · ${amber} amber · ${red} red${untiered > 0 ? ` · ${untiered} untiered` : ''}`;
  return (
    <div className="space-y-2" data-testid="tier-distribution-bar">
      <div
        className="flex h-1.5 w-full overflow-hidden bg-state-hover border border-border-subtle"
        role="img"
        aria-label={`Tier distribution: ${title}`}
        title={title}
      >
        {green > 0 && (
          <div
            data-testid="tier-segment-green"
            className="bg-success"
            style={{ width: `${pct(green)}%` }}
          />
        )}
        {amber > 0 && (
          <div
            data-testid="tier-segment-amber"
            className="bg-warning"
            style={{ width: `${pct(amber)}%` }}
          />
        )}
        {red > 0 && (
          <div
            data-testid="tier-segment-red"
            className="bg-danger"
            style={{ width: `${pct(red)}%` }}
          />
        )}
        {untiered > 0 && (
          <div
            data-testid="tier-segment-untiered"
            className="bg-text-disabled/50"
            style={{ width: `${pct(untiered)}%` }}
          />
        )}
      </div>
      <p className="type-meta uppercase tracking-widest text-text-muted truncate">
        <span className="text-success font-bold">{green}</span> green ·{' '}
        <span className="text-warning font-bold">{amber}</span> amber ·{' '}
        <span className="text-danger font-bold">{red}</span> red
        {untiered > 0 && (
          <>
            {' · '}
            <span className="text-text-muted font-bold">{untiered}</span> untiered
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Readiness-tier sparkbar — mirrors TierDistributionBar but buckets sites
 * by `evidenceReadiness.tier` (excellent / good / fair / thin).
 */
function ReadinessTierBar({
  excellent,
  good,
  fair,
  thin,
  unscored,
  total,
}: {
  excellent: number;
  good: number;
  fair: number;
  thin: number;
  unscored: number;
  total: number;
}) {
  if (total <= 0) {
    return (
      <p className="type-meta uppercase tracking-widest text-text-muted">
        No sites yet
      </p>
    );
  }
  const scored = excellent + good + fair + thin;
  if (scored === 0) {
    return (
      <p className="type-meta uppercase tracking-widest text-text-muted" data-testid="readiness-tier-empty">
        Scores appear after SED bootstrap
      </p>
    );
  }
  const pct = (n: number) => (n / scored) * 100;
  const title = `${excellent} excellent · ${good} good · ${fair} fair · ${thin} thin`;
  return (
    <div className="space-y-2" data-testid="readiness-tier-bar">
      <div
        className="flex h-1.5 w-full overflow-hidden bg-state-hover border border-border-subtle"
        role="img"
        aria-label={`Readiness distribution: ${title}`}
        title={title}
      >
        {excellent > 0 && (
          <div data-testid="readiness-segment-excellent" className="bg-success" style={{ width: `${pct(excellent)}%` }} />
        )}
        {good > 0 && (
          <div data-testid="readiness-segment-good" className="bg-success/60" style={{ width: `${pct(good)}%` }} />
        )}
        {fair > 0 && (
          <div data-testid="readiness-segment-fair" className="bg-warning" style={{ width: `${pct(fair)}%` }} />
        )}
        {thin > 0 && (
          <div data-testid="readiness-segment-thin" className="bg-danger/70" style={{ width: `${pct(thin)}%` }} />
        )}
      </div>
      <p className="type-meta uppercase tracking-widest text-text-muted truncate">
        <span className="text-success font-bold">{excellent + good}</span> ready ·{' '}
        <span className="text-warning font-bold">{fair}</span> fair ·{' '}
        <span className="text-danger font-bold">{thin}</span> thin
        {unscored > 0 && (
          <>
            {' · '}
            <span className="text-text-muted font-bold">{unscored}</span> unscored
          </>
        )}
      </p>
    </div>
  );
}

/** Sort modes for the Top Ranked Sites list. Persisted to localStorage. */
type DashboardSort = 'top-score' | 'most-ready' | 'recently-updated';
const DASHBOARD_SORT_KEY = 'siterag:dashboardSort';

interface SiteRagDashboardProps {
  /** Host callback — opens a site at a specific tab. Used by the
   *  "Add documents to strengthen evidence" CTA on thin sites under
   *  the Most Ready sort. Optional: when omitted the CTA falls back to
   *  opening the site at its default tab. */
  onNavigateToSite?: (siteId: string, tab?: 'documents' | 'reports') => void;
}

function resolveReportBtmStrategy(report: SiteRagReport): 'grid-only' | 'btm-only' | 'hybrid' | 'islanded' | null {
  const legacy = (report as { btmStrategy?: 'grid-only' | 'btm-only' | 'hybrid' | 'islanded' }).btmStrategy;
  if (legacy) return legacy;
  return (
    report.typedPreferencesAtGen?.['power-availability']?.btmStrategy
    ?? report.typedPreferences?.['power-availability']?.btmStrategy
    ?? null
  );
}

export const SiteRagDashboard: React.FC<SiteRagDashboardProps> = ({ onNavigateToSite }) => {
  // Per-slice selectors — Fix_React_Error_185_Prompt step 3.
  const siteRag = useStore(
    useShallow((s) => ({
      reports: s.siteRag.reports,
      sites: s.siteRag.sites,
      isSitesLoaded: s.siteRag.isSitesLoaded,
    })),
  );
  const setActiveRagReport = useStore(s => s.setActiveRagReport);
  const setActiveSite = useStore(s => s.setActiveSite);
  // RN-3 (wave-16) — eagerly hydrate a report on hover so the body
  // renders instantly when the user finally clicks. Idempotent: the
  // store action wraps `dedupeFetch` so repeated hovers collapse into
  // a single in-flight load. Best-effort — failures land silently.
  const prefetchReport = useStore(s => s.prefetchReport);
  const handlePrefetch = React.useCallback((reportId: string) => {
    if (!reportId) return;
    void prefetchReport(reportId).catch((e) => {
      // Defensive — the store action already swallows errors, but
      // keep this guard so a hover never escalates to a runtime
      // error in case the action shape changes.
      console.debug('[dashboard-prefetch] silent', (e as Error)?.message);
    });
  }, [prefetchReport]);
  const allReports = siteRag.reports;
  const allSites = siteRag.sites;
  // W22-6 — KPI tiles used to require BOTH `isSitesLoaded` AND
  // `isReportsLoaded` to flip true before rendering numbers. Live-prod
  // 2026-05-19 walkthrough surfaced "—" placeholders persisting on the
  // dashboard root after `[fetchSites] read 6 doc(s)` resolved — i.e.
  // sites loaded but `isReportsLoaded` was still racing (or never
  // flipped for users whose reports collection-group query failed
  // silently). Since every derived KPI reads from `siteRag.sites` +
  // `siteRag.reports` (default `[]`), the counts are well-defined the
  // moment sites land — reports trickle in afterwards and the tiers
  // update reactively. Gating on `isSitesLoaded` alone removes the
  // stuck-"—" failure mode without re-introducing the flash-zero
  // problem the dual gate was put in place to fix (sites are the
  // primary axis; tier sub-counts simply rise from 0 as reports
  // arrive).
  // `Number.isFinite()` guard is enforced inside the `stats` memo
  // below so any count that ever evaluates to NaN/Infinity falls back
  // to 0 (defence-in-depth per W22 hard rules).
  const isInitialDataLoading = !siteRag.isSitesLoaded;
  const [siteRowMenuId, setSiteRowMenuId] = React.useState<string | null>(null);
  const [siteToDelete, setSiteToDelete] = React.useState<SiteEntity | null>(null);
  // Per-type filter for the Top Ranked Sites + Portfolio Insights blocks.
  // Undefined ('all') means use the latest report on each site regardless
  // of type — back-compat default. Any other value scopes the score to
  // that report type (e.g. only Environmental scores).
  const [filterType, setFilterType] = React.useState<ReportTypeId | undefined>(undefined);
  const [btmStrategyFilter, setBtmStrategyFilter] = React.useState<'all' | 'grid-only' | 'btm-only' | 'hybrid' | 'islanded'>('all');
  const [decisionStateFilter, setDecisionStateFilter] = React.useState<'all' | BtmDecisionState>('all');
  // Sort mode for the Top Ranked Sites list. Persisted under
  // siterag:dashboardSort so the user's choice survives reloads.
  const [sortMode, setSortMode] = React.useState<DashboardSort>(() => {
    if (typeof window === 'undefined') return 'top-score';
    const stored = window.localStorage.getItem(DASHBOARD_SORT_KEY);
    if (stored === 'most-ready' || stored === 'recently-updated' || stored === 'top-score') {
      return stored;
    }
    return 'top-score';
  });
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.setItem(DASHBOARD_SORT_KEY, sortMode); } catch { /* ignore */ }
  }, [sortMode]);

  // Deduplicate: only count latest version per site (group by site name)
  const reports = React.useMemo(() => {
    const siteMap: Record<string, typeof allReports[number]> = {};
    for (const r of allReports) {
      const key = reportSiteGroupKey(r);
      const existing = siteMap[key];
      if (!existing || (r.version || 1) > (existing.version || 1) ||
          ((r.version || 1) === (existing.version || 1) && new Date(r.createdAt) > new Date(existing.createdAt))) {
        siteMap[key] = r;
      }
    }
    return Object.values(siteMap);
  }, [allReports]);

  // DR1 — Calculation KPIs use the latest successfully saved result per site.
  // A newer failed, partial, or metadata-only retry remains visible in report
  // lifecycle/activity surfaces but cannot replace the last usable decision.
  const latestUsableReportPerSite = React.useMemo(() => {
    const bySiteId: Record<string, SiteRagReport> = {};
    for (const site of allSites) {
      const report = latestUsableSavedReportForSite(site, allReports);
      if (report) bySiteId[site.id] = report;
    }
    return bySiteId;
  }, [allReports, allSites]);

  // DR1 — Tier counts use siteBtmVerdictTier (saved exec summary → latest
  // usable saved report).
  // 'none' tier sites are surfaced as `untiered` rather than silently
  // bucketed into red.
  const stats = React.useMemo(() => {
    const sitesWithReports = allSites.filter(s => siteBtmVerdictReport(s, allReports) != null);
    const verdicts = sitesWithReports.map(s => siteBtmVerdictTier(s, allReports));
    // W22-6 — defence-in-depth Number.isFinite() guard: Array.length
    // is always a non-negative integer in practice, but the W22 hard
    // rules require an explicit guard on count math so any future
    // refactor that introduces a non-integer count surfaces as 0
    // (visible, debuggable) rather than NaN (silent + uppercases the
    // tile to "NaN").
    const safe = (n: number): number => (Number.isFinite(n) && n >= 0 ? n : 0);
    return {
      totalSites: safe(allSites.length),
      sitesWithReports: safe(sitesWithReports.length),
      green: safe(verdicts.filter(t => t === 'green').length),
      amber: safe(verdicts.filter(t => t === 'amber').length),
      red: safe(verdicts.filter(t => t === 'red').length),
      untiered: safe(verdicts.filter(t => t === 'none').length),
    };
  }, [allSites, allReports]);

  // Portfolio headline figures for the Aurora-Cockpit KPI hero.
  //  • contractedMw  — Σ effective power across all sites (exact MW when
  //    known, else the bucket midpoint). A site with an undecided power
  //    intent contributes 0 (no signal), so the figure never invents
  //    capacity. `safe()` keeps a pathological NaN out of the tile.
  //  • atRisk        — sites whose latest verdict is amber or red. These
  //    are the conditional / blocked sites the operator must clear; GO-ready
  //    (green) is surfaced separately as the headline advancing count.
  const headlineStats = React.useMemo(() => {
    const safe = (n: number): number => (Number.isFinite(n) && n >= 0 ? n : 0);
    let contractedMw = 0;
    let sitesWithPower = 0;
    for (const s of allSites) {
      const ep = effectivePower(s.projectIntent);
      const mw = ep.mw ?? bucketMidpointMw(ep.bucket);
      if (typeof mw === 'number' && Number.isFinite(mw) && mw > 0) {
        contractedMw += mw;
        sitesWithPower += 1;
      }
    }
    return {
      contractedMw: safe(Math.round(contractedMw)),
      sitesWithPower: safe(sitesWithPower),
    };
  }, [allSites]);

  const portfolioMapPins = React.useMemo(
    () => buildPortfolioMapPins(allSites, allReports),
    [allSites, allReports],
  );

  const btmStats = React.useMemo(() => {
    const latestReports = Object.values(latestUsableReportPerSite);
    const counts: Record<BtmDecisionState, number> = {
      'btm-recommended': 0,
      'grid-recommended': 0,
      'hybrid-candidate': 0,
      'decision-pending': 0,
    };
    for (const report of latestReports) {
      const state = getBtmDecisionState(report);
      counts[state] += 1;
    }
    return counts;
  }, [latestUsableReportPerSite]);

  // Portfolio evidence readiness — average score + tier mix across sites.
  // Replaces the SC2 Site Config completeness KPI, which read as opaque
  // admin telemetry ("0 of 1 · missing context") at portfolio zoom.
  const readinessStats = React.useMemo(() => {
    const tiers: Record<EvidenceReadinessTier, number> = {
      excellent: 0,
      good: 0,
      fair: 0,
      thin: 0,
    };
    let scoreSum = 0;
    let scored = 0;
    let unscored = 0;
    for (const s of allSites) {
      const er = s.evidenceReadiness;
      if (!er || !Number.isFinite(er.score)) {
        unscored += 1;
        continue;
      }
      tiers[er.tier] += 1;
      scoreSum += er.score;
      scored += 1;
    }
    const safe = (n: number): number => (Number.isFinite(n) && n >= 0 ? n : 0);
    const readyCount = safe(tiers.excellent + tiers.good);
    const avgScore = scored > 0 ? Math.round(scoreSum / scored) : null;
    return {
      totalSites: safe(allSites.length),
      scored,
      unscored: safe(unscored),
      readyCount,
      avgScore,
      tiers: {
        excellent: safe(tiers.excellent),
        good: safe(tiers.good),
        fair: safe(tiers.fair),
        thin: safe(tiers.thin),
      },
    };
  }, [allSites]);

  const recentReports = [...reports].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  ).slice(0, 5);

  return (
    <div className="space-y-8">
      {/* DR5 — Zero-state welcome card. When the user has zero sites the
          KPI grid is just five honest zeros, which reads as "broken" rather
          than "empty". Replace it with a single-card welcome + CTA that
          fires the same `datacrate:new-site` window event the File menu
          uses, so we don't have to thread a new prop down from the parent.
          The KPI grid stays hidden until the user creates their first
          site. */}
      {!isInitialDataLoading && stats.totalSites === 0 ? (
        <Card
          className="rounded-lg border-border-subtle bg-state-hover"
          data-testid="dashboard-welcome-empty"
        >
          <CardContent className="p-8 flex items-center justify-between gap-6">
            <div className="space-y-2 min-w-0 flex-1">
              <p className="text-sm font-bold uppercase tracking-widest text-text-primary">
                Welcome — no sites yet.
              </p>
              <p className="text-[11px] text-text-muted uppercase tracking-wider">
                Create your first site to populate the Overview.
              </p>
            </div>
            <Button
              data-testid="dashboard-welcome-cta"
              className="type-meta uppercase tracking-widest shrink-0"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new Event('datacrate:new-site'));
                }
              }}
            >
              + New Site
            </Button>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* ── PORTFOLIO COMMAND CENTER HERO — Aurora Cockpit (.da-tabhead--hero
          + .da-kpis). Replaces the three free-standing KPI Card tiles with
          one atrium banner carrying the four portfolio headline figures
          (contracted capacity · sites in portfolio · GO-ready · at-risk).
          The report-coverage tier sparkbar and the evidence-readiness detail
          remain wired beneath the strip so every site-centric guard (DR5 /
          W22-6 / readiness math) survives the re-skin verbatim. ── */}
      <header className="da-tabhead da-tabhead--hero" data-testid="dashboard-command-hero">
        <div className="da-tabhead__eyebrow">
          <span className="da-tabhead__dot" aria-hidden />
          <span className="da-type-eyebrow">
            <LayoutDashboard className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden />
            Portfolio
            <span className="da-tabhead__ctx"> · {stats.totalSites} site{stats.totalSites === 1 ? '' : 's'}</span>
          </span>
        </div>
        <div className="da-tabhead__top">
          <div className="da-tabhead__titlewrap">
            <h1 className="da-tabhead__title">Command Center</h1>
            <p className="da-type-body da-tabhead__desc">
              {isInitialDataLoading
                ? 'Loading portfolio…'
                : `${stats.green} site${stats.green === 1 ? '' : 's'} clear the readiness gates and are advancing; ${stats.amber + stats.red} need attention across the estate.`}
            </p>
          </div>
        </div>

        {/* KPI strip — the four portfolio headline figures. */}
        <dl className="da-kpis">
          <div className="da-kpi">
            <dt className="da-kpi__k">Contracted capacity</dt>
            <dd className="da-kpi__v">
              <span className="da-kpi__num">
                {isInitialDataLoading ? <span className="text-text-muted/50">—</span> : headlineStats.contractedMw.toLocaleString()}
              </span>
              <span className="da-kpi__unit">MW · {headlineStats.sitesWithPower} sized</span>
            </dd>
          </div>
          <div className="da-kpi">
            <dt className="da-kpi__k">Sites in portfolio</dt>
            <dd className="da-kpi__v">
              <span className="da-kpi__num" data-testid="kpi-total-sites">
                {isInitialDataLoading ? <span className="text-text-muted/50">—</span> : stats.totalSites}
              </span>
              <span className="da-kpi__unit" data-testid="kpi-sites-with-reports">
                {isInitialDataLoading ? '—' : `${stats.sitesWithReports} of ${stats.totalSites} reported`}
              </span>
            </dd>
            {!isInitialDataLoading && (
              <div className="mt-2">
                <TierDistributionBar
                  green={stats.green}
                  amber={stats.amber}
                  red={stats.red}
                  untiered={stats.untiered}
                  total={stats.sitesWithReports}
                />
              </div>
            )}
          </div>
          <div className="da-kpi">
            <dt className="da-kpi__k">GO-ready</dt>
            <dd className="da-kpi__v">
              <span className="da-kpi__num da-kpi__num--accent" data-testid="kpi-go-ready">
                {isInitialDataLoading ? <span className="text-text-muted/50">—</span> : stats.green}
              </span>
              <span className="da-kpi__unit">advancing</span>
            </dd>
          </div>
          <div className="da-kpi" data-testid="kpi-evidence-readiness-card">
            <dt className="da-kpi__k">At-risk</dt>
            <dd className="da-kpi__v">
              <span className="da-kpi__num da-cell-num--danger" data-testid="kpi-at-risk">
                {isInitialDataLoading ? <span className="text-text-muted/50">—</span> : stats.amber + stats.red}
              </span>
              <span className="da-kpi__unit">conditional / blocked</span>
            </dd>
            {/* Evidence-readiness detail — preserved below the at-risk count so
                the readiness math + its test contract survive the re-skin. */}
            {!isInitialDataLoading && (
              readinessStats.totalSites === 0 ? (
                <p className="type-meta uppercase tracking-widest text-text-muted mt-2" data-testid="kpi-evidence-readiness-empty">
                  No sites yet
                </p>
              ) : (
                <div className="mt-2 space-y-1">
                  <p className="type-meta uppercase tracking-widest text-text-muted" data-testid="kpi-evidence-readiness-score">
                    {readinessStats.avgScore != null
                      ? <>Evidence {readinessStats.avgScore} avg</>
                      : <>Evidence <span className="text-text-muted/50">—</span></>}
                  </p>
                  {readinessStats.scored > 0 && (
                    <p className="type-meta uppercase tracking-widest text-text-muted truncate" data-testid="kpi-evidence-readiness-ready">
                      {readinessStats.readyCount} of {readinessStats.totalSites} sites ready for generation
                    </p>
                  )}
                  <ReadinessTierBar
                    excellent={readinessStats.tiers.excellent}
                    good={readinessStats.tiers.good}
                    fair={readinessStats.tiers.fair}
                    thin={readinessStats.tiers.thin}
                    unscored={readinessStats.unscored}
                    total={readinessStats.totalSites}
                  />
                </div>
              )
            )}
          </div>
        </dl>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-btm-kpis">
        <Card className="rounded-lg border-border-subtle bg-state-hover">
          <CardContent className="p-4">
            <p className="type-body font-bold uppercase tracking-widest text-text-muted">BTM recommended sites</p>
            <p className="text-2xl font-bold text-success" data-testid="kpi-btm-recommended">{btmStats['btm-recommended']}</p>
          </CardContent>
        </Card>
        <Card className="rounded-lg border-border-subtle bg-state-hover">
          <CardContent className="p-4">
            <p className="type-body font-bold uppercase tracking-widest text-text-muted">Grid-only recommended</p>
            <p className="text-2xl font-bold text-text-primary" data-testid="kpi-grid-recommended">{btmStats['grid-recommended']}</p>
          </CardContent>
        </Card>
        <Card className="rounded-lg border-border-subtle bg-state-hover">
          <CardContent className="p-4">
            <p className="type-body font-bold uppercase tracking-widest text-text-muted">Hybrid candidates</p>
            <p className="text-2xl font-bold text-warning" data-testid="kpi-hybrid-candidate">{btmStats['hybrid-candidate']}</p>
          </CardContent>
        </Card>
        <Card className="rounded-lg border-border-subtle bg-state-hover">
          <CardContent className="p-4">
            <p className="type-body font-bold uppercase tracking-widest text-text-muted">BTM decision pending</p>
            <p className="text-2xl font-bold text-danger" data-testid="kpi-btm-pending">{btmStats['decision-pending']}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Global Site Map */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-text-primary">Global Portfolio Map</h2>
            </div>
            <Badge variant="outline" className="rounded-md border-border type-meta uppercase tracking-widest" data-testid="map-active-pins">
              {portfolioMapPins.length} Site{portfolioMapPins.length === 1 ? '' : 's'} on Map
            </Badge>
          </div>
          <div className="h-[500px] border border-border-subtle relative">
            {!isInitialDataLoading && portfolioMapPins.length === 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-state-hover pointer-events-none">
                <div className="text-center space-y-2">
                  <Globe className="w-10 h-10 text-text-disabled mx-auto" />
                  <p className="type-meta">
                    {allSites.length === 0
                      ? 'Create a site to see portfolio pins on the map'
                      : 'Add site locations to populate the portfolio map'}
                  </p>
                </div>
              </div>
            )}
            <DashboardMap pins={portfolioMapPins} onPinClick={setActiveSite} />
            
            {/* Map Legend — BTM-centric feasibility / risk tiers */}
            <div className="absolute bottom-4 left-4 z-10">
              <BtmStrategyMapLegend testId="dashboard-map-legend" />
            </div>
          </div>
        </div>

        {/* Recent Activity & Ranked Sites */}
        <div className="space-y-6 min-w-0">
          <div
            className="border border-border-subtle bg-state-hover/40 p-4 space-y-3 min-w-0"
            data-testid="dashboard-ranked-sites-panel"
          >
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-text-primary">
              {sortMode === 'most-ready' ? 'Most Ready Sites'
                : sortMode === 'recently-updated' ? 'Recently Updated Sites'
                : 'Top Ranked Sites'}
            </h2>
            <div
              className="grid grid-cols-2 gap-2 min-w-0"
              data-testid="dashboard-ranked-sites-filters"
            >
              <select
                value={sortMode}
                onChange={e => setSortMode(e.target.value as DashboardSort)}
                className="w-full min-w-0 max-w-full type-meta font-bold uppercase tracking-wider bg-background border border-border px-2 py-1 text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                aria-label="Sort sites"
                data-testid="dashboard-sort-mode"
              >
                <option value="top-score">Top score</option>
                <option value="most-ready">Most ready</option>
                <option value="recently-updated">Recently updated</option>
              </select>
              <select
                value={filterType ?? ''}
                onChange={e => setFilterType((e.target.value as ReportTypeId) || undefined)}
                className="w-full min-w-0 max-w-full type-meta font-bold uppercase tracking-wider bg-background border border-border px-2 py-1 text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                aria-label="Filter top-ranked sites by report type"
                data-testid="dashboard-filter-type"
              >
                <option value="">All types</option>
                {Object.values(REPORT_TYPE_REGISTRY)
                  .filter(t => t.status === 'active' || t.status === 'beta')
                  .map(t => <option key={t.id} value={t.id}>{t.shortLabel}</option>)}
              </select>
              <select
                value={btmStrategyFilter}
                onChange={e => setBtmStrategyFilter(e.target.value as 'all' | 'grid-only' | 'btm-only' | 'hybrid' | 'islanded')}
                className="w-full min-w-0 max-w-full type-meta font-bold uppercase tracking-wider bg-background border border-border px-2 py-1 text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                aria-label="Filter by BTM strategy"
                data-testid="dashboard-filter-btm-strategy"
              >
                <option value="all">All strategies</option>
                <option value="grid-only">Grid-only</option>
                <option value="btm-only">BTM-only</option>
                <option value="islanded">Islanded / off-grid</option>
                <option value="hybrid">Hybrid</option>
              </select>
              <select
                value={decisionStateFilter}
                onChange={e => setDecisionStateFilter(e.target.value as 'all' | BtmDecisionState)}
                className="w-full min-w-0 max-w-full type-meta font-bold uppercase tracking-wider bg-background border border-border px-2 py-1 text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                aria-label="Filter by decision state"
                data-testid="dashboard-filter-decision-state"
              >
                <option value="all">All decisions</option>
                <option value="btm-recommended">BTM recommended</option>
                <option value="grid-recommended">Grid-only recommended</option>
                <option value="hybrid-candidate">Hybrid candidate</option>
                <option value="decision-pending">Decision pending</option>
              </select>
            </div>
            <div className="space-y-2 min-w-0">
              {(() => {
                // Score sites (not reports) so a site with multiple report
                // types reports a single rank. siteSummaryScore filters by
                // the active type when set; otherwise picks the latest
                // report across all types.
                const scored = allSites
                  .map(site => {
                    const summary = siteSummaryScore(site, allReports, filterType);
                    if (!summary) return null;
                    if (btmStrategyFilter !== 'all' && resolveReportBtmStrategy(summary.report) !== btmStrategyFilter) {
                      return null;
                    }
                    const state = getBtmDecisionState(summary.report);
                    if (decisionStateFilter !== 'all' && state !== decisionStateFilter) {
                      return null;
                    }
                    return { site, summary };
                  })
                  .filter((x): x is { site: SiteEntity; summary: NonNullable<ReturnType<typeof siteSummaryScore>> } => x !== null);
                // Apply the active sort mode. compareSitesByReadiness
                // (lib/evidenceReadiness.ts) handles the readiness desc +
                // name tiebreak; recently-updated reads updatedAt off the
                // SiteEntity. Top score is the historical default.
                const sorted = (() => {
                  if (sortMode === 'most-ready') {
                    return [...scored].sort((a, b) => compareSitesByReadiness(a.site, b.site));
                  }
                  if (sortMode === 'recently-updated') {
                    return [...scored].sort((a, b) => {
                      const ta = new Date(a.site.updatedAt ?? 0).getTime();
                      const tb = new Date(b.site.updatedAt ?? 0).getTime();
                      return tb - ta;
                    });
                  }
                  // SF7 — sort by tier first (green > amber > red), then
                  // by internal score within tier (descending). The user
                  // never sees the underlying number, but the ordering is
                  // preserved so sites with the same tier are ranked
                  // intuitively (strongest-of-strong at the top).
                  const tierRank = (t: 'green' | 'amber' | 'red' | 'none'): number =>
                    t === 'green' ? 0 : t === 'amber' ? 1 : t === 'red' ? 2 : 3;
                  return [...scored].sort((a, b) => {
                    const ta = tierFromScore(a.summary.score, a.summary.report.reportType);
                    const tb = tierFromScore(b.summary.score, b.summary.report.reportType);
                    const rankDiff = tierRank(ta) - tierRank(tb);
                    if (rankDiff !== 0) return rankDiff;
                    return b.summary.score - a.summary.score;
                  });
                })();
                const ranked = sorted.slice(0, 3);
                if (ranked.length === 0 && !isInitialDataLoading) {
                  return (
                    <div
                      className="border border-dashed border-border-subtle px-3 py-4 text-center"
                      data-testid="dashboard-ranked-sites-empty"
                    >
                      <p className="type-body uppercase tracking-widest text-text-muted">
                        No sites with {filterType ? REPORT_TYPE_REGISTRY[filterType].shortLabel : 'reports'} yet.
                      </p>
                    </div>
                  );
                }
                return ranked.map(({ site, summary }, i) => {
                  const report = summary.report;
                  const matchedSite = site;
                  // Most-ready sort surfaces sites by evidence depth, so a
                  // "thin" site at the top is exactly the case where the
                  // user should be nudged to upload docs rather than spend
                  // a generation run. Swap the right-hand score block for
                  // a one-click CTA that opens the site at its Documents
                  // tab.
                  const isThin = matchedSite.evidenceReadiness?.tier === 'thin';
                  const showAddDocsCta = sortMode === 'most-ready' && isThin;
                  return (
                    <Card
                      key={report.id}
                      data-testid="dashboard-site-row"
                      data-site-id={matchedSite.id}
                      className="rounded-lg border-border-subtle bg-state-hover hover:bg-state-hover cursor-pointer transition-colors relative"
                      onClick={() => setActiveRagReport(report.id)}
                      onMouseEnter={() => handlePrefetch(report.id)}
                      onFocus={() => handlePrefetch(report.id)}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-8 h-8 flex items-center justify-center bg-state-active text-text-primary font-bold text-xs">
                          0{i + 1}
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="type-section text-text-primary truncate">{report.siteName}</p>
                          <p className="type-meta text-text-muted uppercase tracking-wider truncate">{report.address}</p>
                          <p className="type-meta text-text-muted uppercase tracking-wider truncate" data-testid="dashboard-site-decision-state">
                            {getDecisionStateLabel(getBtmDecisionState(report))}
                          </p>
                          {(() => {
                            const trust = getTrustCue(report);
                            return (
                              <p className="type-meta text-text-muted uppercase tracking-wider truncate" data-testid="dashboard-site-trust-cue">
                                {trust.citedRefs} cited, {trust.providedRefs} provided, {trust.staleLabel}
                              </p>
                            );
                          })()}
                          {/* DSS-1 (2026-05-18) — DataReadinessPill stripped from
                              the Top Ranked Sites row. Per-site evidence readiness
                              lives inside the site landing page; the portfolio
                              dashboard only carries rank + identity + decision state. */}
                        </div>
                        {showAddDocsCta ? (
                          <Button
                            variant="outline"
                            className="type-meta uppercase tracking-widest"
                            data-testid="dashboard-add-docs-cta"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onNavigateToSite) onNavigateToSite(matchedSite.id, 'documents');
                              else setActiveSite(matchedSite.id);
                            }}
                          >
                            Add documents to strengthen evidence
                          </Button>
                        ) : null}
                        {matchedSite && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSiteRowMenuId(siteRowMenuId === matchedSite.id ? null : matchedSite.id);
                            }}
                            onBlur={() => setTimeout(() => setSiteRowMenuId(curr => curr === matchedSite.id ? null : curr), 150)}
                            className="p-1 text-text-secondary hover:text-text-primary"
                            aria-label="Site actions"
                            title="Site actions"
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {matchedSite && siteRowMenuId === matchedSite.id && (
                          <div
                            className="absolute right-2 top-12 z-20 min-w-[160px] bg-background border border-border shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onMouseDown={() => { setSiteRowMenuId(null); setSiteToDelete(matchedSite); }}
                              className="w-full text-left px-3 py-2 type-meta text-danger hover:bg-danger/10 flex items-center gap-2"
                            >
                              <Trash2 className="w-3 h-3" /> Delete site
                            </button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                });
              })()}
            </div>

            <div className="pt-3 border-t border-border-subtle space-y-2 min-w-0" data-testid="top-btm-ready-sites">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-text-secondary">Top BTM-ready sites</h3>
              {(() => {
                const candidates = allSites
                  .map(site => {
                    const summary = siteSummaryScore(site, allReports, filterType);
                    if (!summary) return null;
                    const state = getBtmDecisionState(summary.report);
                    if (state === 'decision-pending') return null;
                    return { site, report: summary.report, readiness: site.evidenceReadiness?.score ?? 0, state };
                  })
                  .filter((row): row is NonNullable<typeof row> => row !== null)
                  .sort((a, b) => b.readiness - a.readiness)
                  .slice(0, 3);
                if (candidates.length === 0) {
                  return (
                    <div
                      className="border border-dashed border-border-subtle px-3 py-3 text-center"
                      data-testid="dashboard-btm-ready-empty"
                    >
                      <p className="type-body uppercase tracking-widest text-text-muted">No BTM-ready sites yet.</p>
                    </div>
                  );
                }
                return candidates.map(row => (
                  <button
                    key={row.site.id}
                    type="button"
                    className="w-full text-left border border-border-subtle bg-background px-3 py-2 hover:bg-state-hover transition-colors"
                    onClick={() => setActiveRagReport(row.report.id)}
                  >
                    <p className="type-body font-bold uppercase tracking-widest text-text-primary truncate">{row.site.name}</p>
                    <p className="type-meta uppercase tracking-widest text-text-muted truncate">
                      {getDecisionStateLabel(row.state)} · readiness {Math.round(row.readiness)}
                    </p>
                  </button>
                ));
              })()}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-text-primary">Recent Evaluations</h2>
            <div className="space-y-3">
              {recentReports.map(report => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-3 border-b border-border-subtle group cursor-pointer hover:bg-state-hover transition-colors"
                  onClick={() => setActiveRagReport(report.id)}
                  onMouseEnter={() => handlePrefetch(report.id)}
                  onFocus={() => handlePrefetch(report.id)}
                  data-testid="dashboard-recent-evaluation-row"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-3.5 h-3.5 text-text-muted" />
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-text-primary uppercase tracking-wider">{report.siteName}</p>
                      <p className="type-meta">
                        {new Date(report.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-primary/0 group-hover:text-primary transition-all" />
                </div>
              ))}
              
              {!isInitialDataLoading && reports.length === 0 && (
                <div className="py-12 text-center space-y-4">
                  <div className="p-4 bg-state-hover rounded-full w-16 h-16 mx-auto flex items-center justify-center">
                    <Search className="w-8 h-8 text-text-disabled" />
                  </div>
                  <p className="type-meta text-text-muted">No reports generated yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Portfolio Insights — aggregated from actual report data.
              Category list is derived from the union of categories present
              in the (filtered) reports, so a non-feasibility filter shows
              the categories THAT type's prompt produces, not the legacy
              feasibility-only ['grid','cooling','site','regulatory','logistics']. */}
          {reports.length > 0 && (() => {
            // Filter reports to the active filterType (if any) before
            // aggregating, so the bars reflect only the slice the user is
            // looking at via the Top Ranked dropdown.
            const insightReports = filterType
              ? reports.filter(r => (r.reportType ?? 'feasibility') === filterType)
              : reports;

            // Build the category list from the reports themselves — preserve
            // first-seen order and pick up label from whichever report
            // mentioned it first.
            // Note: `Map` is shadowed by @vis.gl/react-google-maps' Map at the
            // top of this file — use a plain object keyed by category id.
            const seenLabels: Record<string, string> = {};
            const seenOrder: string[] = [];
            for (const r of insightReports) {
              for (const c of r.categories ?? []) {
                if (!(c.id in seenLabels)) {
                  seenLabels[c.id] = c.label;
                  seenOrder.push(c.id);
                }
              }
            }

            const avgByCategory: Array<{ id: string; label: string; avg: number }> = [];
            for (const id of seenOrder) {
              const scores = insightReports
                .map(r => r.categories?.find(c => c.id === id)?.score ?? 0)
                .filter(s => s > 0);
              const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
              if (avg > 0) avgByCategory.push({ id, label: seenLabels[id], avg });
            }

            // Portfolio risk summary
            const totalRisks = reports.reduce((sum, r) => sum + (r.risks?.length ?? 0), 0);
            const criticalRisks = reports.reduce((sum, r) => sum + (r.risks?.filter((ri: any) => ri.severity === 'critical' || ri.severity === 'high').length ?? 0), 0);

            // DSS-2 (2026-05-18) — `Data Quality · N Verified/Estimated/Degraded`
            // rollup stripped from the Portfolio Insights panel. The chip row
            // didn't drive any portfolio-level decision at this zoom; per-site
            // data quality remains visible inside each site. Underlying
            // `report.dataQuality` field is preserved and still feeds the
            // per-report surfaces.

            const getBarColor = (score: number) => score >= 70 ? '[&>div]:bg-success' : score >= 40 ? '[&>div]:bg-warning' : '[&>div]:bg-danger';
            const getLabel = (score: number) => score >= 70 ? 'Strong' : score >= 40 ? 'Moderate' : 'Weak';
            const getLabelColor = (score: number) => score >= 70 ? 'text-success' : score >= 40 ? 'text-warning' : 'text-danger';

            return (
              <div className="p-6 border border-border-subtle bg-state-hover space-y-5">
                <h3 className="type-section text-text-secondary">Portfolio Insights</h3>

                {/* Category averages */}
                <div className="space-y-3">
                  {avgByCategory.map(cat => (
                    <div key={cat.id} className="space-y-1.5">
                      <div className="flex justify-between type-body uppercase tracking-widest">
                        <span className="text-text-secondary">{cat.label}</span>
                        <span className={`font-bold ${getLabelColor(cat.avg)}`}>{getLabel(cat.avg)} ({cat.avg})</span>
                      </div>
                      <Progress value={cat.avg} className={`h-1 bg-state-hover ${getBarColor(cat.avg)}`} />
                    </div>
                  ))}
                </div>

                {/* Risk summary */}
                <div className="pt-3 border-t border-border-subtle space-y-2">
                  <div className="flex justify-between type-body uppercase tracking-widest">
                    <span className="text-text-secondary">Total Risks Identified</span>
                    <span className="text-text-primary font-bold">{totalRisks}</span>
                  </div>
                  <div className="flex justify-between type-body uppercase tracking-widest">
                    <span className="text-text-secondary">High/Critical Risks</span>
                    <span className={`font-bold ${criticalRisks > 0 ? 'text-danger' : 'text-success'}`}>{criticalRisks}</span>
                  </div>
                  <div className="type-meta uppercase tracking-widest text-text-muted">
                    Decision cues use cited refs, provided refs, and latest key constraint.
                  </div>
                </div>

                {/* DSS-2 (2026-05-18) — Data Quality chip row removed. See
                    rollup comment above the avgByCategory block. */}
              </div>
            );
          })()}
        </div>
      </div>
      </>
      )}

      {/* Site cascade-delete dialog (Top Ranked Sites kebab → Delete site). */}
      {siteToDelete && (
        <DeleteSiteDialog
          site={siteToDelete}
          open={true}
          onClose={() => setSiteToDelete(null)}
          onDeleted={() => setSiteToDelete(null)}
        />
      )}
    </div>
  );
};
